import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { TenantBranding } from '../types';
import { API_URL, getAuthHeaders } from '../utils/api';
import { useUserStore } from '../store/userStore';

interface TenantContextValue {
  branding: TenantBranding;
  /** Ответ `/tenant/branding` пришёл (или не придёт): разделы компании уже известны. */
  isLoaded: boolean;
}

// Fallback branding used until /tenant/branding responds (or if it never does).
// Keeps the app usable when tenant resolution isn't wired up — same UX as
// before multi-tenant was introduced.
const DEFAULT_BRANDING: TenantBranding = {
  companyName: 'SkillScape',
  primaryColor: '#12b886',
  locale: 'ru',
};

const TenantContext = createContext<TenantContextValue | null>(null);

export const useTenant = (): TenantContextValue => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
};

/** Без провайдера (тесты компонентов) — `null`: разделы считаются включёнными. */
export const useOptionalTenant = (): TenantContextValue | null => useContext(TenantContext);

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [branding, setBranding] = useState<TenantBranding>(DEFAULT_BRANDING);
  // Ключ — токен, для которого брендинг уже пришёл: после входа или «войти как» разделы
  // прошлой компании не должны решать, куда пустить.
  const [loadedFor, setLoadedFor] = useState<string | null | undefined>(undefined);
  // Компания берётся из токена, а не из адреса (все компании на skillscape.ru): после входа и
  // выхода брендинг запрашивается заново.
  const token = useUserStore((s) => s.token);

  useEffect(() => {
    // Токен сменился раньше ответа — прошлый запрос отменяем, его ответ (или ошибку) не берём:
    // иначе брендинг и разделы прошлой компании перетёрли бы новые.
    const controller = new AbortController();
    const isStale = () => controller.signal.aborted;
    fetch(`${API_URL}/tenant/branding`, { headers: getAuthHeaders(), signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: Partial<TenantBranding> | null) => {
        if (isStale()) return;
        // Пустые поля организации не затирают умолчания: у организации может не
        // быть своего названия, и тогда в шапке должно остаться «SkillScape», а
        // не пустое место. Нет ответа — SkillScape: после выхода брендинг прошлой компании
        // не должен остаться на странице входа.
        if (!data) return setBranding(DEFAULT_BRANDING);
        const filled = Object.fromEntries(
          Object.entries(data).filter(
            ([, value]) => value !== null && value !== undefined && value !== '',
          ),
        );
        setBranding({ ...DEFAULT_BRANDING, ...filled });
      })
      .catch(() => {
        if (!isStale()) setBranding(DEFAULT_BRANDING);
      })
      .finally(() => {
        if (!isStale()) setLoadedFor(token);
      });
    return () => controller.abort();
  }, [token]);

  return (
    <TenantContext.Provider value={{ branding, isLoaded: loadedFor === token }}>
      {children}
    </TenantContext.Provider>
  );
};
